import dayjs, { type PluginFunc } from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ar';
import 'dayjs/locale/be';
import 'dayjs/locale/ca';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';
import 'dayjs/locale/vi';
import 'dayjs/locale/zh-tw';

dayjs.extend(localizedFormat);

/**
 * Format time method: Get formatted display text based on comparison result
 */

let _locale: string = 'en';

export function setLocale(locale: string | undefined) {
  _locale = locale ?? 'en';
  dayjs.locale(_locale);
}

const localeMap: Record<
  string,
  {
    today: string;
    yesterday: string;
    yesterdayAndTime: string;
    monthAndDay: string;
  }
> = {
  ar: {
    today: 'اليوم',
    yesterday: 'اليوم السابق',
    yesterdayAndTime: 'اليوم السابق, HH:mm',
    monthAndDay: 'D MMMM',
  },
  be: {
    today: 'Сёньня',
    yesterday: 'Вчора',
    yesterdayAndTime: 'Вчора, HH:mm',
    monthAndDay: 'D MMMM',
  },
  ca: {
    today: 'Avui',
    yesterday: 'Ahir',
    yesterdayAndTime: 'Ahir, HH:mm',
    monthAndDay: 'D MMMM',
  },
  cs: {
    today: 'Dnes',
    yesterday: 'Včera',
    yesterdayAndTime: 'Včera, HH:mm',
    monthAndDay: 'D. MMMM',
  },
  en: {
    today: 'Today',
    yesterday: 'Yesterday',
    yesterdayAndTime: '[Yesterday], HH:mm',
    monthAndDay: 'MMMM D',
  },
  vi: {
    today: 'Hôm nay',
    yesterday: 'Hôm qua',
    yesterdayAndTime: 'Hôm qua, HH:mm',
    monthAndDay: 'D MMMM',
  },
  'zh-tw': {
    today: '今天',
    yesterday: '昨天',
    yesterdayAndTime: '昨天HH:mm',
    monthAndDay: 'M月D日',
  },
};

const templateMap = {
  h: 'YYYY-MM-DD HH',
  m: 'YYYY-MM-DD HH:mm',
  s: 'YYYY-MM-DD HH:mm:ss',
};

export type TemplateMapKeys = keyof typeof templateMap;

export type TranslateFn = (key: string) => string;

export function formatDate(
  data?: dayjs.ConfigType,
  to: TemplateMapKeys = 's',
  utc = true,
  template?: string
) {
  const template_ = template || templateMap[to];
  return dayjs(data, { utc }).format(template_);
}

export function formatSeconds(
  seconds: number,
  to?: TemplateMapKeys,
  utc?: boolean,
  template?: string
) {
  return formatDate(seconds, to, utc, template);
}

export const globalUTC: PluginFunc = (option, Dayjs, dayjs) => {
  utc(option, Dayjs, dayjs);

  const proto: any = Dayjs.prototype;

  const oldParse = proto.parse;

  proto.parse = function (cfg: any) {
    cfg = { utc: true, ...cfg };
    oldParse.call(this, cfg);
  };
};

/**
 *
 * @param duration milliseconds
 * @returns '10d:10h:10m'
 */
export function formatDuration(milliseconds: number) {
  const durationInSeconds = Math.floor(milliseconds / 1000);
  const days = Math.floor(durationInSeconds / (60 * 60 * 24));
  const hours = Math.floor((durationInSeconds / (60 * 60)) % 24);
  const minutes = Math.floor((durationInSeconds / 60) % 60);
  const seconds = Math.floor(durationInSeconds % 60);
  let formattedDuration = '';
  const hasDays = days > 0;
  const hasHours = hours > 0;
  if (hasDays) {
    formattedDuration += days.toString() + 'd:';
  }
  if (hasDays || hasHours) {
    formattedDuration += hours.toString().padStart(2, '0') + 'h:';
  }

  formattedDuration += minutes.toString().padStart(2, '0') + 'm';

  if (!hasDays && !hasHours) {
    formattedDuration += ':' + seconds.toString().padStart(2, '0') + 's';
  }
  return formattedDuration;
}

/**
 * Time relation types
 */
export enum TimeRelation {
  /** Today */
  TODAY = 'today',
  /** Yesterday */
  YESTERDAY = 'yesterday',
  /** This week (excluding today and yesterday) */
  THIS_WEEK = 'this_week',
  /** Last week */
  LAST_WEEK = 'last_week',
  /** This month (excluding this week) */
  THIS_MONTH = 'this_month',
  /** Last month */
  LAST_MONTH = 'last_month',
  /** This year (excluding this month) */
  THIS_YEAR = 'this_year',
  /** Last year */
  LAST_YEAR = 'last_year',
  /** Earlier */
  EARLIER = 'earlier',
  /** Future */
  FUTURE = 'future',
}

/**
 * Pure time comparison result (without display text)
 */
export interface TimeCompareResult {
  /** Relation type */
  relation: TimeRelation;
  /** Minutes difference (negative for past, positive for future) */
  minutesDiff: number;
  /** Hours difference (negative for past, positive for future) */
  hoursDiff: number;
  /** Days difference (negative for past, positive for future) */
  daysDiff: number;
  /** Months difference (negative for past, positive for future) */
  monthsDiff: number;
  /** Years difference (negative for past, positive for future) */
  yearsDiff: number;
}

/**
 * Pure comparison method: Compare the relationship between a timestamp and current time
 * @param timestamp The timestamp to compare (milliseconds)
 * @param currentTime Current timestamp (milliseconds), optional, defaults to current time
 * @returns Pure time relationship comparison result (without display text)
 */
export function compareTime(
  timestamp: number,
  currentTime: number = Date.now()
): TimeCompareResult {
  const target = dayjs(timestamp);
  const now = dayjs(currentTime);

  // Calculate differences
  const minutesDiff = target.diff(now, 'minute');
  const hoursDiff = target.endOf('hour').diff(now.endOf('hour'), 'hour');
  const daysDiff = target.endOf('day').diff(now.endOf('day'), 'day');
  const monthsDiff = target.endOf('month').diff(now.endOf('month'), 'month');
  const yearsDiff = target.endOf('year').diff(now.endOf('year'), 'year');

  // Check various same relationships
  const isSameDay = target.isSame(now, 'day');
  const isSameWeek = target.isSame(now, 'week');
  const isSameMonth = target.isSame(now, 'month');
  const isSameYear = target.isSame(now, 'year');

  // Determine relation type
  let relation: TimeRelation;

  if (target.isAfter(now, 'day')) {
    // Future time
    relation = TimeRelation.FUTURE;
  } else if (isSameDay) {
    // Today
    relation = TimeRelation.TODAY;
  } else if (daysDiff === -1) {
    // Yesterday
    relation = TimeRelation.YESTERDAY;
  } else if (isSameWeek) {
    // This week
    relation = TimeRelation.THIS_WEEK;
  } else if (target.isSame(now.subtract(1, 'week'), 'week')) {
    // Last week
    relation = TimeRelation.LAST_WEEK;
  } else if (isSameMonth) {
    // This month
    relation = TimeRelation.THIS_MONTH;
  } else if (target.isSame(now.subtract(1, 'month'), 'month')) {
    // Last month
    relation = TimeRelation.LAST_MONTH;
  } else if (isSameYear) {
    // This year
    relation = TimeRelation.THIS_YEAR;
  } else if (target.isSame(now.subtract(1, 'year'), 'year')) {
    // Last year
    relation = TimeRelation.LAST_YEAR;
  } else {
    // Earlier
    relation = TimeRelation.EARLIER;
  }

  return {
    relation,
    minutesDiff,
    hoursDiff,
    daysDiff,
    monthsDiff,
    yearsDiff,
  };
}

export function formatTimeDisplay(timestamp: number, currentTime: number = Date.now()) {
  const localeFormats = localeMap[_locale?.toLocaleLowerCase() ?? 'en'];
  const target = dayjs(timestamp).locale(_locale ?? 'en');
  const compareResult = compareTime(timestamp, currentTime);
  const year = target.format('YYYY');
  const monthAndDay = target.format('MM-DD');
  const hourAndMinute = target.format('HH:mm');
  const YMDLocale = target.format('LL');
  const YMDHMLocale = target.format('LLL');
  const MDLocale = target.format(localeFormats.monthAndDay);
  const yesterdayAndTimeLocale = target.format(localeFormats.yesterdayAndTime);

  let title = '';
  let fullTitle = '';

  // TODO amo: i18n
  switch (compareResult.relation) {
    case TimeRelation.TODAY:
      title = localeFormats.today;
      fullTitle = hourAndMinute;
      break;
    case TimeRelation.YESTERDAY:
      title = localeFormats.yesterday;
      fullTitle = yesterdayAndTimeLocale;
      break;
    default:
      if (compareResult.yearsDiff === 0) {
        title = MDLocale;
      } else {
        title = YMDLocale;
      }
      fullTitle = YMDHMLocale;
      break;
  }

  return {
    title,
    fullTitle,
    year,
    monthAndDay,
    hourAndMinute,
    YMDLocale,
    YMDHMLocale,
    MDLocale,
    yesterdayAndTimeLocale,
    compareResult,
  };
}

export function formatTimeSimpleDisplay(timestamp: number, currentTime: number = Date.now()) {
  const { compareResult, fullTitle, title } = formatTimeDisplay(timestamp, currentTime);

  switch (compareResult.relation) {
    case TimeRelation.TODAY:
      return fullTitle;
    default:
      return title;
  }
}

export type FormatTimeDisplayResult = ReturnType<typeof formatTimeDisplay>;
